import { useCallback, useState } from 'react';
import {
	updateEVoucher,
	postCustomerAccountData,
	asyncApiCall,
	updateTargetedPromotion,
} from '../api';
import useAuth from '../hooks/useAuth';
import useAccountServiceRequestor from '../hooks/useAccountServiceRequestor';
import { CampaignTypes } from './utils';

const useIssuanceOfferFlow = () => {
	const { getAuthHeaders } = useAuth();
	const { requestorId, accountServiceHeaders } = useAccountServiceRequestor();

	const [issuedCampaignCode, setIssuedCampaignCode] = useState(null);
	const [isIssuanceOfferFetching, setIsIssuanceOfferFetching] = useState(false);
	const [issuanceOffer, setIssuanceOffer] = useState(null);
	const [issuanceOfferError, setIssuanceOfferError] = useState(null);
	const [isAssignIssuanceOfferFetching, setIsAssignIssuanceOfferFetching] =
		useState(false);
	const [isIssuanceOfferAssigned, setIsIssuanceOfferAssigned] = useState(false);
	const [assignIssuanceOfferError, setAssignIssuanceOfferError] =
		useState(null);

	const handleIssuanceOfferSubmit = useCallback(
		async (campaign, { amount } = {}) => {
			setIsIssuanceOfferFetching(true);
			setIssuanceOfferError(null);
			setAssignIssuanceOfferError(null);
			setIssuanceOffer(null);

			setIssuedCampaignCode(campaign.promotionCode);

			try {
				const issuerOffer = {
					campaign_code: campaign.promotionCode,
					description: campaign.campaignName,
					value_currency: 'GBP', // static atm.
					value_amount: amount,
					value_amount_child: 0,
					issued_customer_profile_id: '',
				};

				const apiToCall =
					campaign.campaignType === CampaignTypes.VOUCHER
						? updateEVoucher
						: updateTargetedPromotion;

				const response = await asyncApiCall(
					apiToCall,
					issuerOffer,
					requestorId,
				);

				const issuedOffer = {
					campaignCode: response.campaign_code,
					description: response.description,
					currency: response.value_currency,
					amount: response.value_amount,
					amountChild: response.value_amount_child,
					issuedCustomerProfileId: response.issued_customer_profile_id,
					redeemableCustomerProfileId: response.redeemable_customer_profile_id,
					accountCode: response.account_code,
					channelIdentifier: response.channel_identifier,
					pin: response.pin,
					campaignType: response.campaign_type,
					issuedDate: response.issued_date,
					status: response.status,
					maxAllowedRedemptions: response.max_allowed_redemptions,
					redemptionEffectiveDate: response.redemption_effective_date,
					redemptionExpirationDate: response.redemption_expiration_date,
				};

				setIssuanceOffer(issuedOffer);
			} catch (error) {
				const errorText =
					campaign.campaignType === CampaignTypes.VOUCHER
						? 'eVoucher'
						: 'promotion';
				setIssuanceOfferError(
					error.message || `Failed to generate ${errorText} on issuance`,
				);
			} finally {
				setIsIssuanceOfferFetching(false);
			}
		},
		[requestorId],
	);

	const handleAssignIssuanceOfferSubmit = useCallback(
		async (issuanceOfferData, { customerId } = {}) => {
			setIsAssignIssuanceOfferFetching(true);
			setAssignIssuanceOfferError(null);
			setIsIssuanceOfferAssigned(false);

			try {
				const voucher = {
					code: issuanceOfferData.campaignCode,
					pin: issuanceOfferData.pin,
					description: issuanceOfferData.description,
					price: {
						currency: issuanceOfferData.currency,
						amount: issuanceOfferData.amount,
					},
					expiryDate: issuanceOfferData.redemptionExpirationDate,
					status: issuanceOfferData.status,
				};

				await asyncApiCall(
					postCustomerAccountData,
					voucher,
					{
						...getAuthHeaders(),
						...accountServiceHeaders,
					},
					`/${customerId}/vouchers`,
				);

				setIsIssuanceOfferAssigned(true);
			} catch (error) {
				const errorText =
					issuanceOfferData.campaignType === CampaignTypes.VOUCHER
						? 'eVoucher'
						: 'promotion';
				setAssignIssuanceOfferError(
					error.message || `Failed to assign ${errorText} to customer`,
				);
			} finally {
				setIsAssignIssuanceOfferFetching(false);
			}
		},
		[getAuthHeaders, accountServiceHeaders],
	);

	const handleGenerateOfferCancel = useCallback(() => {
		setIssuanceOffer(null);
		setIsIssuanceOfferFetching(false);
		setIssuanceOfferError(null);
		setAssignIssuanceOfferError(null);
		setIsIssuanceOfferAssigned(false);
		setIsAssignIssuanceOfferFetching(false);
		setIssuedCampaignCode(null);
	}, []);

	return {
		issuedCampaignCode,
		isIssuanceOfferFetching,
		issuanceOffer,
		issuanceOfferError,
		isAssignIssuanceOfferFetching,
		isIssuanceOfferAssigned,
		assignIssuanceOfferError,
		handleIssuanceOfferSubmit,
		handleAssignIssuanceOfferSubmit,
		handleGenerateOfferCancel,
	};
};

export default useIssuanceOfferFlow;
